import { loadingController, modalController, toastController } from '@ionic/core';
import { Component, Element, Event, EventEmitter, Fragment, h, Host, Prop, State } from '@stencil/core';
import { PersonDoc, RoleUtils } from 'common';
import { User } from 'src/global';
import { AuthService } from 'src/global/service/AuthService';
import { PeopleService } from 'src/global/service/PeopleService';

export interface ConcoursePersonUpgradeProps {
  projectId: string;
  person: PersonDoc;
  feature: string;
  featureId: string;
  needsWrite: boolean;
}
@Component({
  tag: 'concourse-person-upgrade',
})
export class ConcoursePersonUpgrade {
  static async presentModal(props: ConcoursePersonUpgradeProps): Promise<{ action: 'upgraded' | 'requested'; person: PersonDoc }> {
    const modal = await modalController.create({
      component: 'concourse-person-upgrade',
      cssClass: 'concourse-modal modal-height-auto',
      backdropDismiss: false,
      componentProps: props,
    });
    return new Promise<{ action: 'upgraded' | 'requested'; person: PersonDoc }>(async resolve => {
      modal.addEventListener('upgraded', (event: CustomEvent) => {
        resolve({ action: 'upgraded', person: event.detail });
        modal.dismiss();
      });

      modal.addEventListener('requested', (event: CustomEvent) => {
        resolve({ action: 'requested', person: event.detail });
        modal.dismiss();
      });

      modal.addEventListener('dismiss', () => {
        resolve(null);
        modal.dismiss();
      });

      await modal.present();
    });
  }

  @Element() hostElement: HTMLElement;
  @Prop() projectId: string;
  @Prop() person: PersonDoc;
  @Prop() feature: string;
  @Prop() featureId: string;
  @Prop() needsWrite: boolean;

  @State() errorMessages: string;
  @State() featureAccessRole: string = '';

  @Event() dismiss: EventEmitter;
  @Event() upgraded: EventEmitter<PersonDoc>;
  @Event() requested: EventEmitter<PersonDoc>;

  get user(): User {
    return globalThis.user;
  }

  get canUpgradePerson(): boolean {
    if (this.needsWrite) {
      return RoleUtils.canWrite(this.projectId, this.feature, this.featureId, this.user.roles);
    } else {
      return RoleUtils.canRead(this.projectId, this.feature, this.featureId, this.user.roles);
    }
  }

  get newRoles(): string[] {
    return Array.from(new Set([...this.person.roles, this.featureAccessRole]));
  }

  private async onUpgradeButtonClick() {
    if (this.canUpgradePerson) {
      await this.updatePermissions();
    } else {
      await this.requestPermissions();
    }
  }

  private async updatePermissions() {
    const loading = await loadingController.create({ message: 'Updating permissions' });
    try {
      loading.present();
      const { message, person } = await AuthService.adminConfirmAccess(this.projectId, this.person._id, this.newRoles);
      toastController.create({ message: message, duration: 2000 }).then(toast => toast.present());
      this.upgraded.emit(person);
    } catch (error) {
      console.error(error);
      this.errorMessages = error.message;
    } finally {
      loading.dismiss();
    }
  }

  private async requestPermissions() {
    const loading = await loadingController.create({ message: 'Requesting permissions' });
    try {
      loading.present();
      await AuthService.requestAccess(this.projectId, this.feature, this.featureAccessRole, this.person);
      this.requested.emit(this.person);
    } catch (error) {
      console.error(error);
      this.errorMessages = error.message;
    } finally {
      loading.dismiss();
    }
  }

  render() {
    return (
      <Host class="concourse-modal">
        <modal-header modalTitle={`Update permissions of ${PeopleService.getFullName(this.person)}`} onDismiss={() => this.dismiss.emit()}></modal-header>
        <div class="modal-content">
          <feature-access-radio-buttons
            projectId={this.projectId}
            feature={this.feature}
            featureId={this.featureId}
            onRoleSelected={e => (this.featureAccessRole = e.detail)}
            needsWrite={this.needsWrite}
          />
        </div>
        <ion-footer>
          {this.errorMessages && <ion-text color="danger">{this.errorMessages}</ion-text>}
          <ion-toolbar>
              <ion-button slot="end" disabled={!this.featureAccessRole} fill="solid" color="primary" shape="round" onClick={() => this.onUpgradeButtonClick()}>
                {this.canUpgradePerson ? 'Update Permissions' : 'Request Permissions'}
              </ion-button>
          </ion-toolbar>
        </ion-footer>
      </Host>
    );
  }
}
